/**
 * PortalPayments - Card management, payment plans, and transaction history
 * Deployed to: frontend/src/pages/portal/PortalPayments.js
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CreditCard, Calendar, DollarSign, Plus, Pause, Play,
  Trash2, AlertCircle, CheckCircle, Clock, RefreshCw, Download
} from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';
import PortalAddCardModal from '../../components/portal/PortalAddCardModal';

const PortalPayments = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('cards');
  const [cards, setCards] = useState([]);
  const [plans, setPlans] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCard, setShowAddCard] = useState(false);
  const [showNewPlan, setShowNewPlan] = useState(false);
  const [cases, setCases] = useState([]);
  const [planForm, setPlanForm] = useState({ case_id: '', amount: '', frequency: 'monthly', start_date: '', notes: '' });

  useEffect(() => { loadData(); }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'cards') {
        const data = await clientPortalService.getSavedCards();
        setCards(data.cards || []);
      } else if (activeTab === 'plans') {
        const data = await clientPortalService.getPaymentPlans();
        setPlans(data.plans || []);
      } else {
        const data = await clientPortalService.getTransactions();
        setTransactions(data.transactions || []);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const removeCard = async (cardId) => {
    if (!window.confirm('Remove this card?')) return;
    try {
      await clientPortalService.removeCard(cardId);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove card');
    }
  };

  const pausePlan = async (planId) => {
    try {
      await clientPortalService.pausePlan(planId);
      loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const resumePlan = async (planId) => {
    try {
      await clientPortalService.resumePlan(planId);
      loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const cancelPlan = async (planId) => {
    if (!window.confirm('Cancel this payment plan?')) return;
    try {
      await clientPortalService.cancelPlan(planId);
      loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const createPlan = async () => {
    try {
      await clientPortalService.createPaymentPlan(planForm);
      setShowNewPlan(false);
      setPlanForm({ case_id: '', amount: '', frequency: 'monthly', start_date: '', notes: '' });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create plan');
    }
  };

  const statusBadge = (status) => {
    const colors = {
      active: 'bg-green-500/20 text-green-300 border-green-500/30',
      paused: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      cancelled: 'bg-red-500/20 text-red-300 border-red-500/30',
      pending_approval: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    };
    return `px-2 py-0.5 rounded-full text-xs border ${colors[status] || colors.active}`;
  };

  const tabs = [
    { id: 'cards', label: 'Saved Cards', icon: CreditCard },
    { id: 'plans', label: 'Payment Plans', icon: Calendar },
    { id: 'history', label: 'Transaction History', icon: DollarSign },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900">
      <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center space-x-4">
          <button onClick={() => navigate('/portal/dashboard')} className="text-virgo-teal-400 hover:text-white"><ArrowLeft size={24} /></button>
          <CreditCard className="text-cyan-400" size={24} />
          <h1 className="text-xl font-bold text-white">Payments & Cards</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                  : 'text-virgo-teal-400 hover:bg-virgo-navy-700'
              }`}>
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
          </div>
        ) : (
          <>
            {/* Cards Tab */}
            {activeTab === 'cards' && (
              <div className="space-y-4">
                {cards.map(card => (
                  <div key={card.card_id} className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-cyan-500/20 rounded-lg"><CreditCard className="text-cyan-400" size={24} /></div>
                      <div>
                        <p className="text-white font-semibold">{card.card_brand} ending in {card.last_four}</p>
                        <p className="text-virgo-teal-500 text-sm">Expires {card.exp_month}/{card.exp_year}</p>
                      </div>
                    </div>
                    <button onClick={() => removeCard(card.card_id)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={18} /></button>
                  </div>
                ))}
                {cards.length === 0 && (
                  <div className="text-center py-12 text-virgo-teal-500">
                    <CreditCard className="mx-auto mb-3 opacity-50" size={48} />
                    <p>No saved cards</p>
                    <p className="text-sm mt-1">Add a card to enable autopay and payment plans</p>
                  </div>
                )}
                <button
                  onClick={() => setShowAddCard(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 rounded-lg font-medium hover:bg-cyan-500/30"
                >
                  <Plus size={18} /> Add a card
                </button>
                <p className="text-virgo-teal-500 text-xs mt-4">You can also add a card while paying an invoice — it will be saved for future use.</p>
              </div>
            )}

            {/* Plans Tab */}
            {activeTab === 'plans' && (
              <div className="space-y-4">
                {plans.map(plan => (
                  <div key={plan.id} className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="text-white font-bold text-lg">${plan.payment_amount.toLocaleString()}</span>
                        <span className="text-virgo-teal-400 text-sm ml-2">/ {plan.frequency}</span>
                      </div>
                      <span className={statusBadge(plan.status)}>
                        {plan.status === 'pending_approval' ? 'Awaiting Approval' : plan.status}
                      </span>
                    </div>
                    {plan.case_number && <p className="text-virgo-teal-500 text-sm">Case: {plan.case_number}</p>}
                    {plan.next_charge_date && (
                      <p className="text-virgo-teal-500 text-sm">Next charge: {new Date(plan.next_charge_date).toLocaleDateString()}</p>
                    )}
                    <p className="text-virgo-teal-500 text-sm">Total paid: ${plan.total_paid.toLocaleString()} ({plan.total_charges} payments)</p>
                    {plan.status !== 'cancelled' && plan.status !== 'pending_approval' && (
                      <div className="flex gap-2 mt-3">
                        {plan.status === 'active' && (
                          <button onClick={() => pausePlan(plan.id)} className="flex items-center gap-1 px-3 py-1 bg-amber-500/20 text-amber-300 rounded text-sm hover:bg-amber-500/30">
                            <Pause size={14} /> Pause
                          </button>
                        )}
                        {plan.status === 'paused' && (
                          <button onClick={() => resumePlan(plan.id)} className="flex items-center gap-1 px-3 py-1 bg-green-500/20 text-green-300 rounded text-sm hover:bg-green-500/30">
                            <Play size={14} /> Resume
                          </button>
                        )}
                        <button onClick={() => cancelPlan(plan.id)} className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-300 rounded text-sm hover:bg-red-500/30">
                          <Trash2 size={14} /> Cancel Plan
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {plans.length === 0 && (
                  <div className="text-center py-12 text-virgo-teal-500">
                    <Calendar className="mx-auto mb-3 opacity-50" size={48} />
                    <p>No payment plans</p>
                    <p className="text-sm mt-1">Set up a payment plan from any unpaid invoice</p>
                  </div>
                )}
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="space-y-3">
                {transactions.map(txn => (
                  <div key={txn.id} className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-500/20 rounded-lg"><CheckCircle className="text-green-400" size={18} /></div>
                      <div>
                        <p className="text-white font-medium">${txn.amount.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                        <p className="text-virgo-teal-500 text-sm">Invoice: {txn.invoice_number}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-virgo-teal-400 text-sm">{txn.payment_date ? new Date(txn.payment_date).toLocaleDateString() : ''}</p>
                        <p className="text-virgo-teal-500 text-xs">{txn.payment_method}</p>
                      </div>
                      <button
                        onClick={async () => {
                          // iOS Safari <a download> no-op — open a tab in-gesture; non-iOS byte-identical.
                          const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) ||
                            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                          const iosWin = isIOS ? window.open('', '_blank') : null;
                          try {
                            const blob = await clientPortalService.downloadReceipt(txn.id);
                            if (isIOS) {
                              const iosUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                              if (iosWin) { iosWin.location = iosUrl; }
                              else { alert('Please allow pop-ups for this site to open the receipt, then tap again.'); }
                              setTimeout(() => window.URL.revokeObjectURL(iosUrl), 60000);
                              return;
                            }
                            const url = window.URL.createObjectURL(new Blob([blob]));
                            const a = document.createElement('a');
                            a.href = url; a.download = `receipt-${txn.id}.pdf`; a.click();
                            window.URL.revokeObjectURL(url);
                          } catch (err) { alert('Failed to download receipt'); }
                        }}
                        className="p-2 text-cyan-400 hover:text-cyan-300 hover:bg-virgo-navy-600 rounded"
                        title="Download Receipt"
                      >
                        <Download size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                {transactions.length === 0 && (
                  <div className="text-center py-12 text-virgo-teal-500">
                    <DollarSign className="mx-auto mb-3 opacity-50" size={48} />
                    <p>No transactions yet</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>

      <PortalAddCardModal
        isOpen={showAddCard}
        onClose={() => setShowAddCard(false)}
        onAdded={loadData}
      />
    </div>
  );
};

export default PortalPayments;
